import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { PageTitleWithHelp, CardTitleWithHelp } from "@/components/ui/title-help";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import {
  FLOOR_SCAN_INPUT_FIELD_PROPS,
  handleFloorSerialEnterKey,
  normalizeFloorSerialInput,
  readFloorSerialFromInput,
} from "@/lib/floorSerialScan";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { WIRELESS_CHECK_LABOR_PAGE } from "@/lib/labor/laborPageKey";
import { playFailureSoundAndNavigate } from "@/lib/labor/laborTimeoutHandlers";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import {
  playPairFailureSound,
  playPairQuestionSuccessSound,
  playPairSuccessSound,
} from "@/lib/floorStationPairAudio";
import { cn } from "@/lib/utils";

import { GpsCheckElapsed } from "./GpsCheckElapsed";
import { WirelessCheckOverrideModal } from "./WirelessCheckOverrideModal";
import { WirelessCheckQcIncidentModal } from "./WirelessCheckQcIncidentModal";
import {
  scanWirelessCheckDevice,
  type WirelessCheckDevice,
  type WirelessCheckScanResponse,
} from "./wirelessCheckApi";

const PAGE_HELP =
  "Scan wireless device label serials after Configuration and before Cal+Test. The hub reads production wireless portal check-ins since this configuration episode (date assembled) and reports whether the modem checked in and whether any check-in has non-zero GPS coordinates, matching the SDTestApp wireless gate.";

function formatDateTime(value: string | null): string {
  if (value == null || value.trim() === "") return "—";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Date(parsed).toLocaleString();
}

function statusTone(ok: boolean): "success" | "danger" {
  return ok ? "success" : "danger";
}

function WirelessCheckLaborTracker() {
  const navigate = useNavigate();
  const labor = useLaborSession({
    page: WIRELESS_CHECK_LABOR_PAGE,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Wireless check",
    autoStart: true,
    requirePersistedClockIn: true,
    onIdleTimeout: () => playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onPersistedSessionLost: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onSessionEndedRemotely: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onStopped: () => playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const snapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Wireless check",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, snapshot);
  return null;
}

function ResultRow({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
      {ok ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
      ) : (
        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" aria-hidden />
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="mt-0.5 text-sm text-slate-600">{detail}</p>
      </div>
      <Badge tone={statusTone(ok)} className="ml-auto shrink-0">
        {ok ? "Yes" : "No"}
      </Badge>
    </div>
  );
}

function WirelessCheckResultCard({
  result,
  onOpenQcIncident,
  onOpenOverride,
}: {
  result: WirelessCheckScanResponse;
  onOpenQcIncident: () => void;
  onOpenOverride: () => void;
}): JSX.Element {
  const device = result.device;
  const portal = device.portal;
  const episode = device.episode;
  const ready =
    episode.passed ||
    (portal.hasWirelessCheckin && portal.hasValidGps && device.portalLookupError == null);

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Portal check-ins are loaded from the wireless device records database since this episode's configuration time (date assembled).">
          {device.deviceLabelSerial}
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap gap-2 text-sm text-slate-600">
          {device.finishedSku != null ? <Badge tone="neutral">{device.finishedSku}</Badge> : null}
          {device.imei != null && device.imei.trim() !== "" ? (
            <Badge tone="neutral">IMEI {device.imei}</Badge>
          ) : null}
          {device.stationName != null ? <Badge tone="info">{device.stationName}</Badge> : null}
          {episode.attemptCount > 0 ? (
            <Badge tone="neutral">
              {episode.passed ? "Passed" : "Checks recorded"}: {episode.attemptCount}
            </Badge>
          ) : null}
          {result.attempt.attemptNumber != null ? (
            <Badge tone={result.attempt.outcome === "pass" ? "success" : "warning"}>
              Check {result.attempt.attemptNumber}
              {result.attempt.outcome != null ? ` — ${result.attempt.outcome}` : ""}
            </Badge>
          ) : null}
          {episode.passed && episode.passSource === "override" ? (
            <Badge tone="info">Passed via administrator override</Badge>
          ) : null}
          {ready ? <Badge tone="success">Ready for Cal+Test</Badge> : <Badge tone="warning">Needs attention</Badge>}
        </div>

        <GpsCheckElapsed lastCheckStartedAt={episode.lastCheckStartedAt ?? result.attempt.lastCheckStartedAt} />

        {result.attempt.operatorMessage != null && result.attempt.operatorMessage.trim() !== "" ? (
          <p
            className={cn(
              "rounded-md border px-3 py-2 text-sm",
              result.attempt.outcome === "pass" || episode.passed
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : result.attempt.cooldownBlocked
                  ? "border-sky-200 bg-sky-50 text-sky-950"
                  : "border-amber-200 bg-amber-50 text-amber-900",
            )}
          >
            {result.attempt.operatorMessage}
          </p>
        ) : null}

        {result.attempt.requireQcIncident && !episode.passed ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex min-h-10 items-center rounded-md bg-rose-700 px-4 text-sm font-medium text-white hover:bg-rose-800"
              onClick={onOpenQcIncident}
            >
              Log QC incident
            </button>
            <button
              type="button"
              className="inline-flex min-h-10 items-center rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-50"
              onClick={onOpenOverride}
            >
              Administrator override
            </button>
          </div>
        ) : null}

        {device.portalLookupError != null ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {device.portalLookupError}
          </p>
        ) : null}

        <div className="space-y-2">
          <ResultRow
            label="Wireless portal check-in since configure"
            ok={portal.hasWirelessCheckin}
            detail={
              portal.hasWirelessCheckin
                ? `${portal.checkinCount} check-in${portal.checkinCount === 1 ? "" : "s"} since ${formatDateTime(device.configuredAt)}. Latest ${formatDateTime(portal.latestCheckinAt)}.`
                : `No portal check-ins since configure at ${formatDateTime(device.configuredAt)}.`
            }
          />
          <ResultRow
            label="Non-zero GPS since configure"
            ok={portal.hasValidGps}
            detail={
              portal.hasValidGps
                ? `Best GPS ${portal.latestValidGpsLat}, ${portal.latestValidGpsLong} at ${formatDateTime(portal.latestValidGpsAt)}.`
                : "No check-in with GPS coordinates other than 0,0 in this window."
            }
          />
        </div>
      </CardBody>
    </Card>
  );
}

export function WirelessCheckPage(): JSX.Element {
  const [serialInput, setSerialInput] = useState("");
  const [lastResult, setLastResult] = useState<WirelessCheckScanResponse | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [qcModalOpen, setQcModalOpen] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const serialInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const scanMutation = useMutation({
    mutationFn: (serial: string) => scanWirelessCheckDevice(serial),
    onSuccess: (data) => {
      setScanError(null);
      setLastResult(data);
      const passed = data.device.episode.passed || data.attempt.episodePassed;
      if (passed) {
        playPairSuccessSound();
      } else if (data.attempt.outcome === "fail" || data.attempt.cooldownBlocked) {
        playPairFailureSound();
      } else {
        playPairQuestionSuccessSound();
      }
      if (data.attempt.requireQcIncident && !data.device.episode.passed) {
        setQcModalOpen(true);
      }
      setSerialInput("");
      serialInputRef.current?.focus();
    },
    onError: (error) => {
      playPairFailureSound();
      const message = error instanceof Error ? error.message : "Scan failed.";
      setScanError(message);
      setLastResult(null);
    },
  });

  const submitSerial = useCallback(
    (raw: string) => {
      const serial = normalizeFloorSerialInput(raw);
      if (serial === "") return;
      scanMutation.mutate(serial);
    },
    [scanMutation],
  );

  useFloorPageScanCapture({
    enabled: !scanMutation.isPending,
    inputRef: serialInputRef,
    onScan: (value) => submitSerial(value),
  });

  const submitFromInput = useCallback(() => {
    submitSerial(readFloorSerialFromInput(serialInputRef.current, serialInput));
  }, [serialInput, submitSerial]);

  const activeDevice: WirelessCheckDevice | null = lastResult?.device ?? null;
  const qcAttemptNumber =
    lastResult?.attempt.attemptNumber ?? lastResult?.device.episode.attemptCount ?? 5;

  const applyDeviceAfterOverride = useCallback((device: WirelessCheckDevice) => {
    setLastResult((previous) =>
      previous != null
        ? {
            ...previous,
            device,
            attempt: {
              ...previous.attempt,
              episodePassed: true,
              passSource: "override",
              operatorMessage: "Administrator override recorded. Unit is ready for Cal+Test.",
              requireQcIncident: false,
            },
          }
        : null,
    );
    playPairSuccessSound();
  }, []);

  return (
    <div className="space-y-6">
      <WirelessCheckLaborTracker />
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Wireless check</PageTitleWithHelp>
      <LaborStationOperatorPresence stationPage={WIRELESS_CHECK_LABOR_PAGE} />

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Scan the device label serial. Only wireless SKUs (IMEI / ICCID devices) belong on this station.">
            Scan device
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <form
            ref={formRef}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              submitFromInput();
            }}
          >
            <div className="min-w-0 flex-1">
              <Label htmlFor="wireless-check-serial">Device label serial</Label>
              <Input
                id="wireless-check-serial"
                ref={serialInputRef}
                value={serialInput}
                onChange={(event) => setSerialInput(event.target.value)}
                onKeyDown={(event) => handleFloorSerialEnterKey(event, submitFromInput)}
                disabled={scanMutation.isPending}
                {...FLOOR_SCAN_INPUT_FIELD_PROPS}
                {...FLOOR_PAGE_SCAN_TARGET_PROPS}
              />
            </div>
            <button
              type="submit"
              disabled={scanMutation.isPending}
              className={cn(
                "inline-flex min-h-12 items-center justify-center rounded-md bg-slate-900 px-5 text-sm font-medium text-white",
                "hover:bg-slate-800 disabled:opacity-50",
              )}
            >
              {scanMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Looking up…
                </>
              ) : (
                "Check"
              )}
            </button>
          </form>
          {scanError != null ? (
            <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
              {scanError}
            </p>
          ) : null}
        </CardBody>
      </Card>

      {lastResult != null ? (
        <WirelessCheckResultCard
          result={lastResult}
          onOpenQcIncident={() => setQcModalOpen(true)}
          onOpenOverride={() => setOverrideModalOpen(true)}
        />
      ) : null}

      <WirelessCheckQcIncidentModal
        open={qcModalOpen}
        device={activeDevice}
        attemptNumber={qcAttemptNumber}
        failureModeId={lastResult?.attempt.suggestedFailureModeId ?? null}
        failureModeName={lastResult?.attempt.suggestedFailureModeName ?? null}
        onClose={() => setQcModalOpen(false)}
        onSubmitted={() => {
          setQcModalOpen(false);
        }}
      />
      <WirelessCheckOverrideModal
        open={overrideModalOpen}
        device={activeDevice}
        onClose={() => setOverrideModalOpen(false)}
        onSuccess={applyDeviceAfterOverride}
      />
    </div>
  );
}
